import { PexIcon } from '../../components/icons/PexIcon'
import { Badge, type BadgeProps } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { DataTableShell } from '../../components/ui/data-table-shell'
import { DateRangePicker } from '../../components/ui/date-range-picker'
import { EmptyState } from '../../components/ui/empty-state'
import { Field } from '../../components/ui/field'
import { FilterPanel } from '../../components/ui/filter-panel'
import { Input } from '../../components/ui/input'
import { PageHeader } from '../../components/ui/page-header'
import { Select } from '../../components/ui/select'
import { formatOrderRef, formatWorkOrderRef } from './workOrderDisplay'
import {
  formatDate,
  parsePlainDate,
  serializePlainDate,
} from '../../lib/datetime'
import { useT } from '../../i18n'
import { SUPPLY_WORK_ORDERS_STRINGS } from '../../i18n/supply/work-orders'
import type {
  WorkOrder,
  WorkOrderFulfillmentMode,
  WorkOrderStatus,
} from '../../mocks/data/work-orders'

export type SupplyWorkOrderQueue =
  | 'all'
  | 'assigned'
  | 'active'
  | 'dispatched'
  | 'completed'

export type SupplyDesktopWorkOrderFilters = {
  search: string
  fulfillmentMode: WorkOrderFulfillmentMode | ''
  dateFrom: string
  dateTo: string
  overdueOnly: boolean
}

type SupplyDesktopWorkOrdersProps = {
  displayName: string
  workOrders: WorkOrder[]
  total: number
  overdueCount: number
  queue: SupplyWorkOrderQueue
  filters: SupplyDesktopWorkOrderFilters
  loading: boolean
  error: boolean
  refreshing: boolean
  now: number
  onQueueChange: (queue: SupplyWorkOrderQueue) => void
  onFiltersChange: (filters: SupplyDesktopWorkOrderFilters) => void
  onReset: () => void
  onRefresh: () => void
  onRetry: () => void
  onOpenWorkOrder: (id: string) => void
}

const STATUS_VARIANT: Record<
  WorkOrderStatus,
  NonNullable<BadgeProps['variant']>
> = {
  assigned: 'neutral',
  acknowledged: 'info',
  in_production: 'warning',
  dispatched_to_warehouse: 'info',
  received_at_warehouse: 'success',
  dispatched_to_customer: 'success',
  cancelled: 'destructive',
}

const DAY_MS = 24 * 60 * 60 * 1000

export function SupplyDesktopWorkOrders({
  displayName,
  workOrders,
  total,
  overdueCount,
  queue,
  filters,
  loading,
  error,
  refreshing,
  now,
  onQueueChange,
  onFiltersChange,
  onReset,
  onRefresh,
  onRetry,
  onOpenWorkOrder,
}: SupplyDesktopWorkOrdersProps) {
  const t = useT(SUPPLY_WORK_ORDERS_STRINGS)
  const detail = t.workOrderDetail
  const mobile = t.myWorkOrders.mobile
  const desktop = t.myWorkOrders.desktop

  const statusLabels: Record<WorkOrderStatus, string> = {
    assigned: detail.statusLabel.assigned,
    acknowledged: detail.statusLabel.acknowledged,
    in_production: detail.statusLabel.inProduction,
    dispatched_to_warehouse: detail.statusLabel.dispatchedToWarehouse,
    received_at_warehouse: detail.statusLabel.receivedAtWarehouse,
    dispatched_to_customer: detail.statusLabel.dispatchedToCustomer,
    cancelled: detail.statusLabel.cancelled,
  }

  const queues: Array<{ value: SupplyWorkOrderQueue; label: string }> = [
    { value: 'all', label: mobile.queues.all },
    { value: 'assigned', label: mobile.queues.assigned },
    { value: 'active', label: mobile.queues.active },
    { value: 'dispatched', label: mobile.queues.dispatched },
    { value: 'completed', label: mobile.queues.completed },
  ]

  const update = <K extends keyof SupplyDesktopWorkOrderFilters>(
    key: K,
    value: SupplyDesktopWorkOrderFilters[K],
  ) => onFiltersChange({ ...filters, [key]: value })

  return (
    <section
      className="hidden min-w-0 space-y-[var(--page-section-gap)] md:block"
      data-testid="supply-work-orders-desktop"
    >
      <PageHeader
        title={t.myWorkOrders.pageTitle}
        description={desktop.description(displayName)}
        actions={
          <>
            <div className="flex items-stretch divide-x divide-[var(--color-border)] overflow-hidden rounded-[var(--radius-control)] border border-[var(--color-border)] bg-[var(--color-surface)]">
              <div className="flex min-w-32 items-center gap-2 px-3 py-1">
                <PexIcon name="production" size="sm" tone="muted" decorative />
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--color-text-muted)]">
                    {desktop.total}
                  </div>
                  <div className="text-base font-semibold tabular-nums">{total}</div>
                </div>
              </div>
              <div className="flex min-w-28 items-center gap-2 px-3 py-1">
                <PexIcon
                  name="warning"
                  size="sm"
                  tone={overdueCount > 0 ? 'default' : 'muted'}
                  decorative
                />
                <span
                  className={
                    overdueCount > 0
                      ? 'text-sm font-semibold text-[var(--color-danger)]'
                      : 'text-sm font-medium text-[var(--color-text-muted)]'
                  }
                >
                  {desktop.overdueMetric(overdueCount)}
                </span>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={refreshing}
              onClick={onRefresh}
            >
              <PexIcon
                name="refresh"
                size="xs"
                className={'mr-1' + (refreshing ? ' animate-spin' : '')}
                decorative
              />
              {t.myWorkOrders.actions.refresh}
            </Button>
          </>
        }
      />

      <nav
        aria-label={mobile.queueLabel}
        className="scroll-hint-x overflow-x-auto"
      >
        <div className="inline-flex min-w-max items-center gap-0.5 rounded-[var(--radius-control)] border border-[var(--color-border)] bg-[var(--color-surface)] p-1 shadow-[var(--shadow-nav-active)]">
          {queues.map((item) => {
            const active = queue === item.value
            return (
              <button
                key={item.value}
                type="button"
                aria-label={item.label}
                aria-pressed={active}
                onClick={() => onQueueChange(item.value)}
                className={
                  'inline-flex min-h-7 items-center whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)] ' +
                  (active
                    ? 'bg-[var(--color-action)] text-[var(--color-action-foreground)]'
                    : 'text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)]')
                }
              >
                {item.label}
                {active && (
                  <span className="ml-1.5 rounded-full bg-white/15 px-1.5 py-0.5 text-[10px] tabular-nums">
                    {workOrders.length}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </nav>

      <FilterPanel
        layout="toolbar"
        className="[&_[data-testid=filter-panel-fields]]:min-[1400px]:grid-cols-[minmax(220px,2fr)_minmax(150px,1fr)_minmax(210px,1.4fr)_auto_auto]"
        actions={
          <Button type="button" variant="outline" size="sm" onClick={onReset}>
            {desktop.reset}
          </Button>
        }
      >
        <Field id="supply-desktop-search" label={desktop.searchLabel}>
          <Input
            type="search"
            value={filters.search}
            placeholder={desktop.searchPlaceholder}
            onChange={(event) => update('search', event.target.value)}
          />
        </Field>
        <Field id="supply-desktop-route" label={mobile.routeLabel}>
          <Select
            value={filters.fulfillmentMode}
            onChange={(event) =>
              update(
                'fulfillmentMode',
                event.target.value as WorkOrderFulfillmentMode | '',
              )
            }
          >
            <option value="">{mobile.allRoutes}</option>
            <option value="hub_via_nj">{mobile.route.hubViaNj}</option>
            <option value="direct_dropship">
              {mobile.route.directDropship}
            </option>
          </Select>
        </Field>
        <DateRangePicker
          label={mobile.dueDate}
          value={{
            start: parsePlainDate(filters.dateFrom),
            end: parsePlainDate(filters.dateTo),
          }}
          onChange={(range) =>
            onFiltersChange({
              ...filters,
              dateFrom: serializePlainDate(range.start),
              dateTo: serializePlainDate(range.end),
            })
          }
        />
        <label className="flex min-h-9 items-end gap-2 pb-1 text-xs font-semibold text-[var(--color-text)]">
          <input
            type="checkbox"
            checked={filters.overdueOnly}
            className="h-4 w-4 rounded border-[var(--input-border)] accent-[var(--color-action)]"
            onChange={(event) => update('overdueOnly', event.target.checked)}
          />
          {mobile.overdueOnly}
        </label>
      </FilterPanel>

      <DataTableShell>
        {loading ? (
          <div className="px-5 py-14 text-center text-sm text-[var(--color-text-muted)]">
            {t.myWorkOrders.states.loading}
          </div>
        ) : error ? (
          <EmptyState
            icon="warning"
            title={t.myWorkOrders.states.error}
            action={
              <Button type="button" variant="outline" size="sm" onClick={onRetry}>
                {mobile.retry}
              </Button>
            }
          />
        ) : workOrders.length === 0 ? (
          <EmptyState
            icon="inbox"
            title={t.myWorkOrders.table.emptyState.title}
            description={t.myWorkOrders.table.emptyState.description}
          />
        ) : (
          <table>
            <thead>
              <tr>
                <th>{t.myWorkOrders.table.columnHeaders.woId}</th>
                <th>{t.myWorkOrders.table.columnHeaders.product}</th>
                <th className="text-right">
                  {t.myWorkOrders.table.columnHeaders.qty}
                </th>
                <th>{t.myWorkOrders.table.columnHeaders.customerOrderRef}</th>
                <th>{desktop.columns.route}</th>
                <th>{t.myWorkOrders.table.columnHeaders.assignedAt}</th>
                <th>{desktop.columns.due}</th>
                <th>{t.myWorkOrders.table.columnHeaders.status}</th>
              </tr>
            </thead>
            <tbody>
              {workOrders.map((workOrder) => {
                const daysOverdue = Math.ceil(
                  (now - new Date(workOrder.due_date).getTime()) / DAY_MS,
                )
                const terminal = [
                  'received_at_warehouse',
                  'dispatched_to_customer',
                  'cancelled',
                ].includes(workOrder.state)
                const overdue = daysOverdue > 0 && !terminal

                return (
                  <tr key={workOrder.id}>
                    <td>
                      <button
                        type="button"
                        aria-label={mobile.openWorkOrder(formatWorkOrderRef(workOrder.id))}
                        title={workOrder.id}
                        className="whitespace-nowrap font-mono text-xs font-semibold text-[var(--color-text)] hover:underline focus-visible:rounded-sm focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)]"
                        onClick={() => onOpenWorkOrder(workOrder.id)}
                      >
                        {formatWorkOrderRef(workOrder.id)}
                      </button>
                    </td>
                    <td className="max-w-[24rem] py-2">
                      <button
                        type="button"
                        className="block w-full min-w-0 text-left focus-visible:rounded-sm focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)]"
                        onClick={() => onOpenWorkOrder(workOrder.id)}
                      >
                        <span className="block truncate text-sm font-semibold text-[var(--color-text)]">
                          {workOrder.product_name}
                        </span>
                        <span className="block truncate text-xs text-[var(--color-text-muted)]">
                          {workOrder.product_id}
                        </span>
                        {workOrder.notes && (
                          <span className="block truncate text-[11px] italic text-[var(--color-text-muted)]">
                            {workOrder.notes}
                          </span>
                        )}
                      </button>
                    </td>
                    <td className="text-right font-semibold tabular-nums">
                      {workOrder.quantity}
                    </td>
                    <td
                      className="whitespace-nowrap font-mono text-xs text-[var(--color-text)]"
                      title={workOrder.customer_order_ref}
                    >
                      {formatOrderRef(workOrder.customer_order_ref)}
                    </td>
                    <td>
                      <span className="rounded bg-[var(--color-surface-muted)] px-1.5 py-0.5 text-xs text-[var(--color-text)]">
                        {workOrder.fulfillment_mode === 'hub_via_nj'
                          ? mobile.route.hubViaNj
                          : mobile.route.directDropship}
                      </span>
                    </td>
                    <td className="whitespace-nowrap text-xs text-[var(--color-text-muted)]">
                      {formatDate(workOrder.assigned_at)}
                    </td>
                    <td
                      className={
                        overdue
                          ? 'whitespace-nowrap text-xs font-semibold text-[var(--color-danger)]'
                          : 'whitespace-nowrap text-xs text-[var(--color-text)]'
                      }
                    >
                      {overdue
                        ? mobile.overdue(daysOverdue)
                        : formatDate(workOrder.due_date)}
                    </td>
                    <td>
                      <Badge variant={STATUS_VARIANT[workOrder.state]}>
                        {statusLabels[workOrder.state]}
                      </Badge>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </DataTableShell>
    </section>
  )
}
